import React, { useState, useEffect } from "react"
import Header from "../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Paper, Typography, Grid, Card, Stack, Snackbar, Tooltip, Button } from "@mui/material"
import {
  Home,
  CompassCalibration,
  North,
  South,
  East,
  West,
  ArrowUpward,
  ArrowDownward,
  Visibility,
} from "@mui/icons-material"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import CustomBackdrop from "../../../components/CustomBackdrop"
import Swal from "sweetalert2"
import CustomAutocomplete from "../../../components/CustomAutocomplete"
import CustomTextFieldReadable from "../../../components/CustomTextFieldReadable"
import MapaUbicacion from "../Components/MapaUbicacion"
import { api } from "../../../api"

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const BuscarCabeceraUnicaPredio = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const predioData = location.state // Datos del predio desde la acción de ver

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Obtener el ID del predio desde el state
  const preid = predioData?.preid

  // Estado para almacenar los datos completos del predio
  const [predioDetalle, setPredioDetalle] = useState(null)
  const [tiposPredios, setTiposPredios] = useState([])

  // Estado para el formulario (solo visualización)
  const [formData, setFormData] = useState({
    preid: "",
    preidmat: "",
    precatastro: "",
    tippreid: null,
    preunidad: "",
    preubicacion: "",
    presuperficie: "",
    prelindnorte: "",
    preobslindnorte: "",
    prelindsur: "",
    preobslindsur: "",
    prelindeste: "",
    preobslindeste: "",
    prelindoeste: "",
    preobslindoeste: "",
    prelindoarriba: "",
    prelindoabajo: "",
    prealicuota: "0.00",
    precuerpodif: "",
    preestado: "",
  })

  const [coordenadas, setCoordenadas] = useState({
    lat: "",
    lng: "",
    precision: "",
  })

  // Validar que llegaron datos
  useEffect(() => {
    if (!predioData) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se encontró información del predio",
      }).then(() => {
        navigate(-1)
      })
    }
  }, [predioData, navigate])

  // Cargar tipos de predios para poder mostrar el label correcto
  useEffect(() => {
    const fetchTiposPredios = async () => {
      try {
        // Usar la misma API que ya tienes para tipos de predios
        const response = await api.get("/ActivacionDePredios/getAllTiposPrediosCB")
        if (response.data?.data) {
          setTiposPredios(response.data.data)
        }
      } catch (error) {
        console.error("Error cargando tipos de predios:", error)
      }
    }

    fetchTiposPredios()
  }, [])

  // Cargar datos específicos del predio
  useEffect(() => {
    const fetchPredioDetalle = async () => {
      if (!preid) return

      setLoading(true)
      setError(null)

      try {
        // Usar la API getSpecificCabeceraPredio ya creada
        const response = await api.get("/ActivacionDePredios/getSpecificCabeceraPredio", {
          params: { preid },
        })

        if (response.data?.data) {
          const predio = response.data.data
          setPredioDetalle(predio)

          // Formatear coordenadas si existen
          if (predio.precordenadas) {
            const [lat, lng, precision] = predio.precordenadas.split(";")
            setCoordenadas({
              lat: lat || "",
              lng: lng || "",
              precision: precision || "",
            })
          }

          // Buscar el label del tipo de predio
          let tippreidLabel = null
          if (predio.tippreid && tiposPredios.length > 0) {
            const tipoEncontrado = tiposPredios.find((t) => t.value === predio.tippreid)
            tippreidLabel = tipoEncontrado || { value: predio.tippreid, label: predio.tippreid }
          } else {
            tippreidLabel = { value: predio.tippreid, label: predio.tippreid }
          }

          setFormData({
            preid: predio.preid || "",
            preidmat: predio.preidmat || "",
            precatastro: predio.precatastro || "",
            tippreid: tippreidLabel,
            preunidad: predio.preunidad || "",
            preubicacion: predio.preubicacion || "",
            presuperficie: predio.presuperficie || "",
            prelindnorte: predio.prelindnorte || "",
            preobslindnorte: predio.preobslindnorte || "",
            prelindsur: predio.prelindsur || "",
            preobslindsur: predio.preobslindsur || "",
            prelindeste: predio.prelindeste || "",
            preobslindeste: predio.preobslindeste || "",
            prelindoeste: predio.prelindoeste || "",
            preobslindoeste: predio.preobslindoeste || "",
            prelindoarriba: predio.prelindoarriba || "",
            prelindoabajo: predio.prelindoabajo || "",
            prealicuota: predio.prealicuota?.toString() || "0.00",
            precuerpodif: predio.precuerpodif || "",
            preestado: predio.preestado || "",
          })
        } else {
          setError("No se encontraron datos del predio")
        }
      } catch (error) {
        console.error("Error cargando predio:", error)
        setError(error.response?.data?.message || "Error al cargar los datos del predio")
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar la información del predio",
        })
      } finally {
        setLoading(false)
      }
    }

    if (tiposPredios.length > 0 || preid) {
      fetchPredioDetalle()
    }
  }, [preid, tiposPredios])

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Consulta de Predio</b>
        </div>

        <CustomBackdrop isLoading={loading} />

        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error" align="center">
              {error}
            </Typography>
          </Box>
        )}

        <Box sx={StyledRoot}>
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3, background: "white" }}>
            {/* Información General del Predio */}
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "primary.main", mb: 3, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Home /> Información General del Predio
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable disabled label="Estado" value={formData.preestado} />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable fullWidth label="Matrícula" value={formData.preid} />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable fullWidth label="ID Matriz" value={formData.preidmat} />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable fullWidth label="Código Catastral" value={formData.precatastro} />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable
                  fullWidth
                  label="Tipo de Predio"
                  value={formData.tippreid?.label || formData.tippreid?.value || ""}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable fullWidth label="Unidad" value={formData.preunidad} />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable fullWidth label="Ubicación" value={formData.preubicacion} />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable fullWidth label="Superficie" value={formData.presuperficie} />
              </Grid>

              {/* Tercera fila: Alícuota y Cuerpo Diferente */}
              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable fullWidth label="Alícuota" value={formData.prealicuota} />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextFieldReadable fullWidth label="Cuerpo Diferente" value={formData.precuerpodif} />
              </Grid>

              {/* Sección de Linderos */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mt: 2, mb: 2, color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}
                >
                  <CompassCalibration /> Linderos del Predio
                </Typography>
              </Grid>

              {/* Norte */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <North color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Norte
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <CustomTextFieldReadable
                      fullWidth
                      label="Descripción del Lindero Norte"
                      value={formData.prelindnorte}
                      multiline
                      rows={2}
                    />
                    <CustomTextFieldReadable
                      fullWidth
                      label="Observación Técnica Norte"
                      value={formData.preobslindnorte}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Sur */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <South color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Sur
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <CustomTextFieldReadable
                      fullWidth
                      label="Descripción del Lindero Sur"
                      value={formData.prelindsur}
                      multiline
                      rows={2}
                    />
                    <CustomTextFieldReadable
                      fullWidth
                      label="Observación Técnica Sur"
                      value={formData.preobslindsur}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Este */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <East color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Este
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <CustomTextFieldReadable
                      fullWidth
                      label="Descripción del Lindero Este"
                      value={formData.prelindeste}
                      multiline
                      rows={2}
                    />
                    <CustomTextFieldReadable
                      fullWidth
                      label="Observación Técnica Este"
                      value={formData.preobslindeste}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Oeste */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <West color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Oeste
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <CustomTextFieldReadable
                      fullWidth
                      label="Descripción del Lindero Oeste"
                      value={formData.prelindoeste}
                      multiline
                      rows={2}
                    />
                    <CustomTextFieldReadable
                      fullWidth
                      label="Observación Técnica Oeste"
                      value={formData.preobslindoeste}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Arriba y Abajo */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <ArrowUpward color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Arriba
                    </Typography>
                  </Box>
                  <CustomTextFieldReadable
                    fullWidth
                    label="Descripción del Lindero Arriba"
                    value={formData.prelindoarriba}
                    multiline
                    rows={2}
                  />
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <ArrowDownward color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Abajo
                    </Typography>
                  </Box>
                  <CustomTextFieldReadable
                    fullWidth
                    label="Descripción del Lindero Abajo"
                    value={formData.prelindoabajo}
                    multiline
                    rows={2}
                  />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                  Ubicación del Predio
                </Typography>
                <MapaUbicacion
                  label={"Ubicación del Predio"}
                  value={coordenadas}
                  onChange={() => {}} // Vacío porque es solo lectura
                  isMobile={false}
                  modo="manual"
                  readonly={true} // Asumiendo que MapaUbicacion soporta esta prop
                />
                {coordenadas.lat && coordenadas.lng ? (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                    Coordenadas: {coordenadas.lat}, {coordenadas.lng}{" "}
                    {coordenadas.precision && `(Precisión: ${coordenadas.precision})`}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                    No hay coordenadas registradas
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </div>
    </ThemeProvider>
  )
}

export default BuscarCabeceraUnicaPredio
